"use client";

/**
 * Portfolio 弹窗内附件列表：上传、校验、+Upload、initialFiles ingest。
 *
 * +Upload 分端（见 portfolio/filePick/shared/portfolioFilePickStrategy）：
 * - Android：`pickMoreViaSession` → mounted input + session + prepare
 * - iOS / Web：`clickHiddenFileInput` + `onChange`（Sender 对齐）
 *
 * 开弹窗前的选图不在此 hook，见 `pickPortfolioFiles`。
 *
 * @see apps/token-seek/features/20260610-portfolio-file-pick-platform-strategy/SPEC.md
 */
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMemoizedFn } from "ahooks";
import { OspToast } from "@ospweb/sdk-ui";
import log from "logbeacon";
import type { PortfolioUploadType } from "@/src/components/PortfolioScreenshotModal/types";
import type { SenderAttachment } from "@/src/components/Sender/type";
import { uploadAttachmentFiles } from "@/src/utils/attachmentUpload";
import {
  clickHiddenFileInput,
  FILE_PICK_PRESETS,
  type PrepareFileInputParams,
} from "@/src/lib/bridge/prepareFileInput";
import { getBridge } from "@/src/lib/bridge/bridge";
import {
  buildPortfolioPdfPickPreset,
  computePortfolioImageFingerprint,
  createPortfolioFileInputChangeHandler,
  getPortfolioUploadText,
  isPortfolioUploadFileOversized,
  isPortfolioUploadFileValid,
  pickPortfolioFilesFromMountedInput,
  PORTFOLIO_MAX_ATTACHMENTS,
  PORTFOLIO_MAX_PDF_SIZE_MB,
  PORTFOLIO_PDF_ACCEPT,
  preparePortfolioUploadFiles,
  resolvePortfolioFilePickStrategy,
  resolvePortfolioUploadAuthHeaders,
  shouldUsePortfolioPickSessionForMore,
} from "./portfolio";

export {
  arePortfolioUploadAuthHeadersReady,
  PORTFOLIO_MAX_ATTACHMENTS,
  PORTFOLIO_MAX_IMAGES,
} from "./portfolio";

export type UsePortfolioScreenshotImagesParams = {
  uploadType?: PortfolioUploadType;
  authHeaders: Record<string, string>;
  fingerprint?: string | number | null;
  isLoggedIn: boolean;
  hasFileUploadAccess: boolean;
  openLoginModal?: () => void;
  initialFiles?: File[];
  onImagesFingerprintChange?: () => void;
};

export function usePortfolioScreenshotImages({
  uploadType = "image",
  authHeaders,
  fingerprint,
  isLoggedIn,
  hasFileUploadAccess,
  openLoginModal,
  initialFiles,
  onImagesFingerprintChange,
}: UsePortfolioScreenshotImagesParams) {
  const photoInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<SenderAttachment[]>([]);
  const imagesRef = useRef<SenderAttachment[]>([]);
  const fingerprintRef = useRef("");
  const initialHandledRef = useRef(false);
  const initialIngestInFlightRef = useRef(false);
  const pickMoreViaSessionRef = useRef(false);
  const copy = getPortfolioUploadText({ uploadType });

  const isUploading = images.some((item) => item.status === "uploading");

  const imageFingerprint = useMemo(
    () => computePortfolioImageFingerprint(images),
    [images],
  );

  const notifyFingerprintChange = useMemoizedFn((nextFingerprint: string) => {
    if (fingerprintRef.current && fingerprintRef.current !== nextFingerprint) {
      onImagesFingerprintChange?.();
    }
    fingerprintRef.current = nextFingerprint;
  });

  const ensureCanPick = useMemoizedFn((): boolean => {
    if (!isLoggedIn) {
      openLoginModal?.();
      return false;
    }
    if (!hasFileUploadAccess) {
      return false;
    }
    return true;
  });

  const handleFilesSelected = useMemoizedFn(
    async (
      selected: File[],
      options?: { fromPrePick?: boolean; skipPrepare?: boolean },
    ): Promise<boolean> => {
      if (!selected.length) {
        return false;
      }
      if (!isLoggedIn) {
        openLoginModal?.();
        return false;
      }
      if (!options?.fromPrePick && !hasFileUploadAccess) {
        return false;
      }

      let nextSelected = selected;

      const unsupported = nextSelected.filter(
        (file) => !isPortfolioUploadFileValid(file, uploadType),
      );
      if (unsupported.length > 0) {
        OspToast.error({ content: copy.formatErrorToast });
        nextSelected = nextSelected.filter((file) =>
          isPortfolioUploadFileValid(file, uploadType),
        );
        if (nextSelected.length === 0) {
          return false;
        }
      }

      const oversized = nextSelected.filter((file) =>
        isPortfolioUploadFileOversized(file, uploadType),
      );
      if (oversized.length > 0) {
        OspToast.error({ content: copy.oversizedToast });
        nextSelected = nextSelected.filter(
          (file) => !isPortfolioUploadFileOversized(file, uploadType),
        );
        if (nextSelected.length === 0) {
          return false;
        }
      }

      const currentCount = imagesRef.current.length;
      const remaining = PORTFOLIO_MAX_ATTACHMENTS - currentCount;
      if (remaining <= 0) {
        OspToast.error({ content: copy.maxCountToast });
        return false;
      }
      if (nextSelected.length > remaining) {
        OspToast.warning({ content: copy.maxCountTruncateToast });
        nextSelected = nextSelected.slice(0, remaining);
      }

      if (!options?.skipPrepare) {
        nextSelected = await preparePortfolioUploadFiles(
          uploadType,
          nextSelected,
        );
      }
      nextSelected = nextSelected.filter((file) => file.size > 0);
      if (!nextSelected.length) {
        log.error("[PortfolioScreenshot] empty file payload after prepare");
        return false;
      }

      const batchId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const placeholders: SenderAttachment[] = nextSelected.map((file) => ({
        type: "file",
        filename: file.name,
        mediaType: file.type || undefined,
        size: file.size,
        url: "",
        status: "uploading",
        _batchId: batchId,
      }));

      log.info("[PortfolioScreenshot] queue upload", {
        count: nextSelected.length,
        fromPrePick: options?.fromPrePick ?? false,
        names: nextSelected.map((file) => file.name),
      });

      setImages((prev) => {
        const next = [...prev, ...placeholders];
        imagesRef.current = next;
        notifyFingerprintChange(computePortfolioImageFingerprint(next));
        return next;
      });

      try {
        const uploadAuthHeaders =
          await resolvePortfolioUploadAuthHeaders(authHeaders);
        log.info("[PortfolioScreenshot] upload auth ready", {
          ready: Object.keys(uploadAuthHeaders).length > 0,
        });

        const uploaded = await uploadAttachmentFiles({
          files: nextSelected,
          authHeaders: uploadAuthHeaders,
          fingerprint,
        });

        setImages((prev) => {
          const next = [...prev];
          let uploadIdx = 0;
          for (let i = 0; i < next.length && uploadIdx < uploaded.length; i++) {
            if (
              next[i]._batchId === batchId &&
              next[i].status === "uploading"
            ) {
              const item = uploaded[uploadIdx++];
              next[i] = {
                ...next[i],
                filename: item.filename,
                mediaType: item.mediaType || next[i].mediaType,
                size: item.size,
                url: item.url,
                status: "done",
                _batchId: undefined,
              };
            }
          }
          imagesRef.current = next;
          notifyFingerprintChange(computePortfolioImageFingerprint(next));
          return next;
        });
      } catch (error) {
        log.error("[PortfolioScreenshot] upload error", error);
        setImages((prev) => {
          const next = prev.map((item) =>
            item._batchId === batchId && item.status === "uploading"
              ? { ...item, status: "error" as const, _batchId: undefined }
              : item,
          );
          imagesRef.current = next;
          notifyFingerprintChange(computePortfolioImageFingerprint(next));
          return next;
        });
      }

      return true;
    },
  );

  const clickFileInput = useMemoizedFn(
    async (
      inputRef: React.RefObject<HTMLInputElement | null>,
      preset: PrepareFileInputParams,
    ) => {
      if (!ensureCanPick()) {
        return;
      }
      await clickHiddenFileInput(getBridge(), inputRef, preset);
    },
  );

  /** Android-only：弹窗内追加选图，须在 session 内先 prepare 再清 input */
  const pickMoreViaSession = useMemoizedFn(async () => {
    if (!ensureCanPick()) {
      return;
    }
    if (imagesRef.current.length >= PORTFOLIO_MAX_ATTACHMENTS) {
      return;
    }

    const inputRef = uploadType === "pdf" ? documentInputRef : photoInputRef;
    const input = inputRef.current;
    if (!input) {
      return;
    }

    pickMoreViaSessionRef.current = true;

    try {
      const picked = await pickPortfolioFilesFromMountedInput({
        uploadType,
        inputRef,
        settleFiles: async (pickedFiles) => {
          if (!pickedFiles.length) {
            return null;
          }
          const prepared = await preparePortfolioUploadFiles(
            uploadType,
            pickedFiles,
          );
          return prepared.length > 0 ? prepared : null;
        },
      });
      if (picked?.length) {
        await handleFilesSelected(picked, { skipPrepare: true });
      }
    } finally {
      pickMoreViaSessionRef.current = false;
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  });

  /** +Upload 入口：按平台走 session 或 Sender 式 click */
  const handlePickMore = useMemoizedFn(async () => {
    if (!ensureCanPick()) {
      return;
    }
    if (imagesRef.current.length >= PORTFOLIO_MAX_ATTACHMENTS) {
      return;
    }

    if (
      shouldUsePortfolioPickSessionForMore(resolvePortfolioFilePickStrategy())
    ) {
      await pickMoreViaSession();
      return;
    }

    if (uploadType === "pdf") {
      await clickFileInput(documentInputRef, buildPortfolioPdfPickPreset());
      return;
    }
    await clickFileInput(photoInputRef, FILE_PICK_PRESETS.photo);
  });

  const handlePhotoChange = useMemoizedFn(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (pickMoreViaSessionRef.current) {
        return;
      }
      await createPortfolioFileInputChangeHandler(
        photoInputRef,
        uploadType,
        handleFilesSelected,
      )(event);
    },
  );

  const handleDocumentChange = useMemoizedFn(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (pickMoreViaSessionRef.current) {
        return;
      }
      await createPortfolioFileInputChangeHandler(
        documentInputRef,
        uploadType,
        handleFilesSelected,
      )(event);
    },
  );

  const handleRemove = useMemoizedFn((index: number) => {
    setImages((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      imagesRef.current = next;
      notifyFingerprintChange(computePortfolioImageFingerprint(next));
      return next;
    });
  });

  const readyAttachmentUrls = useMemo(
    () =>
      images
        .filter((item) => item.status === "done" && item.url)
        .map((item) => item.url),
    [images],
  );

  useEffect(() => {
    if (initialHandledRef.current || initialIngestInFlightRef.current) {
      return;
    }
    if (!initialFiles?.length || !isLoggedIn) {
      return;
    }

    initialIngestInFlightRef.current = true;
    log.info("[PortfolioScreenshot] ingest initial files", {
      count: initialFiles.length,
    });

    void handleFilesSelected(initialFiles, { fromPrePick: true }).then(
      (queued) => {
        initialIngestInFlightRef.current = false;
        if (queued) {
          initialHandledRef.current = true;
        }
      },
    );
  }, [handleFilesSelected, initialFiles, isLoggedIn]);

  return {
    images,
    imageFingerprint,
    isUploading,
    readyAttachmentUrls,
    /** @deprecated Use readyAttachmentUrls */
    readyImageUrls: readyAttachmentUrls,
    photoInputRef,
    documentInputRef,
    handlePickMore,
    handlePhotoChange,
    handleDocumentChange,
    handleRemove,
    canAddMore: images.length < PORTFOLIO_MAX_ATTACHMENTS,
    attachmentCount: images.length,
    maxAttachments: PORTFOLIO_MAX_ATTACHMENTS,
    uploadType,
    pdfAccept: PORTFOLIO_PDF_ACCEPT,
    maxPdfSizeMb: PORTFOLIO_MAX_PDF_SIZE_MB,
  };
}
